import { SceneEmptyState } from './scene-editors/sceneEmptyState';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import OverlayPanel from './overlayPanel';
import CommandMenu from './scene-editors/commandMenu';
import { SceneEditorAction } from './scene-editors/sceneEditorActionBar';
import { EmptyNodeIcon, EyeMixedIcon, MeshNodeIcon } from '../../../../widgets/icons/scene';
import DragIcon from 'src/shared/material_icons/action/drag_indicator.svg';
import LockIcon from 'src/shared/material_icons/action/lock.svg';
import LockOpenIcon from 'src/shared/material_icons/action/lock_open.svg';
import EyeIcon from 'src/shared/material_icons/action/visibility.svg';
import EyeOffIcon from 'src/shared/material_icons/action/visibility_off.svg';
import ChevronDownIcon from 'src/shared/material_icons/navigation/expand_more.svg';
import ChevronRightIcon from 'src/shared/material_icons/navigation/chevron_right.svg';
import { setComposerRowDragImage } from '../configurator-composer/composer/helpers/composerDragImage';
import { panel } from 'src/components/pages/productConfiguration/steps/configurator-components/scene-properties-panel';
import { T } from '../../../../../shared/helpers';
import { keyText } from './keyText';
import { useInlineRename } from './useInlineRename';

// Un contenitore è un Empty, non una cartella: una trasformazione, un'origine e
// tre assi. Aperto o chiuso non cambia cosa è — a dirlo è la freccia della riga —
// quindi il glifo è lo stesso, come nel prototipo.
const ICONS = {
    folderClosed: <EmptyNodeIcon />,
    folderOpen: <EmptyNodeIcon />,
    file: <MeshNodeIcon />,
    eye: <EyeIcon />,
    eyeOff: <EyeOffIcon />,
    eyeMixed: <EyeMixedIcon />,
    locked: <LockIcon />,
    unlocked: <LockOpenIcon />
};

/**
 * LA VISIBILITÀ DI UN GRUPPO NON È SUA: è il riassunto di quello che contiene. Un contenitore
 * non è una cosa che si vede — è il posto dove stanno le cose che si vedono — quindi il suo
 * occhio legge i figli e li comanda tutti insieme.
 *
 * `some` è uno stato vero e va detto: dentro un gruppo è normale averne spento uno solo, e un
 * occhio acceso su quel gruppo direbbe una cosa falsa.
 */
const leavesOf = (node: any): any[] => (node.children?.length ? node.children.flatMap((child: any) => leavesOf(child)) : [node]);

// NASCONDERE UN RAMO È NASCONDERLO TUTTO, contenitore compreso. Prendendo solo le foglie, un
// contenitore che ha anche una geometria sua — nei modelli importati capita — restava a schermo
// mentre l'occhio diceva che il ramo era spento. Il lucchetto invece resta sulle foglie: bloccare
// è togliere la pescabilità, e un nodo senza superficie non si pesca comunque.
const branchOf = (node: any): any[] => [node, ...(node.children ?? []).flatMap((child: any) => branchOf(child))];

const groupState = (nodes: any[], isOn: (node: any) => boolean): 'all' | 'some' | 'none' => {
    const on = nodes.filter(isOn).length;

    return on === nodes.length ? 'all' : on === 0 ? 'none' : 'some';
};

const isShown = (leaf: any) => leaf.visible !== false;
const isShut = (leaf: any) => leaf.locked === true;

/**
 * UNA FABBRICA, NON UNA FOTOGRAFIA. Chi costruisce l'albero può consegnare il pannello di una riga
 * come funzione, e quella funzione vuol dire «chiedimelo quando ti serve»: il momento in cui serve
 * è ogni ridisegno, perché fra l'uno e l'altro le cose si muovono da sole — il gizmo trascina
 * l'oggetto in scena e il motore non lo dice a nessuno.
 *
 * Chiamandola una volta sola, al clic, l'Inspector si teneva i numeri di quell'istante: spostavi
 * l'oggetto a schermo e i campi restavano dov'erano, come due strumenti che misurano cose diverse.
 */
const LiveComponent = ({ render }: { render: () => React.ReactNode }) => <>{render()}</>;

// Il pannello di una riga esce da qui e da nessun altro posto: chi lo consegna a mano, prima o poi,
// ne consegna uno solo dei due tipi e la colonna di destra torna a invecchiare.
const publishable = (component: any): React.ReactNode => (typeof component === 'function' ? <LiveComponent render={component} /> : component);

/** Sei fasi da 180ms — acceso e spento tre volte. Lo legge sia il timer sia la durata di una fase. */
const BLINK_DURATION = 1080;

const DragPreviewLabel = styled.div<{ x: number; y: number }>`
    position: fixed;
    top: ${(props) => props.y}px;
    left: ${(props) => props.x}px;
    pointer-events: none;
    background: ${(props) => props.theme.app.textColor};
    color: ${(props) => props.theme.app.backgroundColor};
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    z-index: 9999;
`;

// LA FRECCETTA DI UN RAMO, ed è esportata perché la usa anche la vista a GRIGLIA dei materiali:
// là un rientro non esiste, e aprire o chiudere un gruppo è l'unico modo che la griglia ha di
// mostrarlo come una cosa sola. Due triangoli disegnati due volte sarebbero due cose da tenere
// LO STESSO BOTTONE DEI COMANDI DI RIGA, col glifo Material del Composer: due disegni diversi per
// lo stesso gesto — aprire un ramo — si leggono come due gesti diversi.
export const TreeTwisty = styled.button<{ $isOpen: boolean }>`
    display: inline-flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    margin-right: 0;
    padding: 0;
    border: 0;
    border-radius: 5px;
    background: transparent;
    color: ${(props) => props.theme.app.textColor};
    cursor: pointer;
    user-select: none;

    svg {
        width: 17px;
        height: 17px;
    }

    /* Vale anche qui: il glifo a contorno tiene il suo tratto, quello Material si riempie. */
    svg:not([stroke]) {
        fill: currentColor;
    }

    &:hover {
        background: #e5e7eb;
    }

    &:focus-visible {
        outline: 2px solid ${(props) => props.theme.app.secondary};
        outline-offset: 1px;
    }
`;

/* Il posto vuoto di una riga senza rami, così i nomi restano incolonnati. */
export const TreeTwistySlot = styled.span`
    display: inline-flex;
    flex: none;
    width: 28px;
    height: 28px;
`;

/* La maniglia: si trascina da qui, non dalla riga. Sulla riga il puntatore ha già da scegliere,
   rinominare e aprire, e un trascinamento che parte da ovunque parte anche per sbaglio. */
export const TreeDragHandle = styled(TreeTwisty).attrs({ $isOpen: false })`
    cursor: grab;

    &:active {
        cursor: grabbing;
    }
`;

// IL FONDO DI UNA RIGA HA UNA SORGENTE SOLA, perché il lampeggio deve saper tornare esattamente
// dov'era: scritto due volte, la riga finirebbe di lampeggiare su un colore diverso da quello che
// aveva prima.
const rowBackground = (props: { $isDragover: boolean; $selected: boolean; theme: any }) =>
    props.$isDragover || props.$selected ? `${props.theme.app.secondary}14` : 'transparent';

// IL LAMPEGGIO — «è questa». Sei fasi da 180ms: acceso e spento tre volte, che è quel tanto che
// l'occhio segue senza doverlo cercare.
//
// SPENTO È TRASPARENTE, e non il fondo che la riga aveva. Alternare fra il lavato della riga
// scelta e uno appena più carico non si vedeva: su una riga già accesa — e quando si chiede
// «dove sta?» la riga è quasi sempre quella scelta — i due toni erano la stessa cosa. Perdere e
// riprendere il fondo è il segno che si legge da un metro, ed è anche ciò che fa il prototipo.
const blinkRow = keyframes`
    0%, 49.9% { background: var(--row-blink-hot); }
    50%, 100% { background: transparent; }
`;

const TreeNodeContainer = styled.div<{
    $depth: number;
    $isDragging: boolean;
    $isDragover: boolean;
    $selected: boolean;
    $isVisible: boolean;
    $blinking?: boolean;
}>`
    cursor: pointer;
    display: flex;
    align-items: center;

    // L'ALTEZZA DELLA RIGA È DICHIARATA, non dedotta dal glifo che ci capita dentro. Dedotta,
    // valeva ventiquattro pixel — nome, icona e occhio incollati — e un albero di venti mesh
    // diventava un blocco di testo in cui le righe si contano solo rileggendole. Trentaquattro è
    // la misura con cui una riga di elenco si distingue dalla vicina senza che serva un filo a
    // dividerle, ed è anche il pavimento comodo per un bersaglio da colpire col puntatore.
    position: relative;
    min-height: 34px;
    // Le stesse misure delle righe del Composer: pastiglia con sei pixel di respiro ai lati.
    padding: 0 6px;
    border-radius: 8px;
    // Declared, not inherited from the page: the Composer rows carry the same 13px, so the two
    // editors read as one tree. Inheriting gave 14px here and nothing matched the panel around it.
    font-size: 13px;
    // IL RIENTRO SI SCRIVE QUI E BASTA. Lo scriveva anche ChildrenContainer, che è
    // annidato: i due si sommavano a ogni livello, e a profondità quattro il nome del
    // nodo finiva oltre il bordo destro del pannello. Restavano l'occhio e il glifo, e
    // sembrava che le mesh non avessero un nome.
    // Quattordici e non venti: il pannello è largo poco più di duecento pixel, e il
    // rientro è un'informazione — non deve costare più della cosa che indenta.
    padding-left: ${(props) => props.$depth * 14 + 6}px;
    --row-blink-hot: ${(props) => props.theme.app.secondary}66;

    background: ${rowBackground};
    opacity: ${(props) => (props.$isDragging ? 0.5 : props.$isVisible ? 1 : 0.4)};

    // IL SEGNO DELLA RIGA SCELTA È UN TRATTINO DENTRO LA PASTIGLIA, come nel Composer: un filo
    // attaccato al bordo apparteneva alla colonna, non alla riga.
    &::before {
        position: absolute;
        top: 6px;
        bottom: 6px;
        left: 0;
        display: ${(props) => (props.$selected ? 'block' : 'none')};
        width: 2.5px;
        background: ${(props) => props.theme.app.secondary};
        border-radius: 2px;
        content: '';
    }

    // Il rilascio «dentro» si dice con un anello, non con un fondo più carico: il fondo lo usa
    // già la riga scelta, e i due si somigliavano troppo.
    ${(props) => props.$isDragover && `box-shadow: inset 0 0 0 2px ${props.theme.app.secondary};`}

    &:hover {
        background: ${(props) => props.theme.app.backgroundToolbar};
    }

    ${(props) =>
        props.$blinking &&
        css`
            animation: ${blinkRow} 360ms steps(1, end) 3;
        `}
`;

const TreeNodeInnerContainer = styled.div`
    min-height: 100%;
    display: flex;
    flex-direction: column;
    & > :first-child {
        margin-top: 10px;
    }
`;

// LA NOTA DEI DUE GESTI STA IN CODA ALLE RIGHE, DENTRO L'ELENCO — è dove la mette il mockup, ed
// è dove stanno le righe di cui parla. In fondo al PANNELLO invece finiva sotto il vuoto: il
// contenitore dell'albero arriva sempre in fondo (è quel vuoto a deselezionare), quindi la nota
// si staccava dalle righe e sembrava una barra di stato.
const SelectionHint = styled.p`
    flex: none;
    margin: 10px 0 0;
    padding: 0 4px;
    color: ${(props) => props.theme.app.textColor};
    opacity: 0.55;
    font-family: ${(props) => props.theme.app.font};
    font-size: 11px;
`;

// Raggruppa i figli e non li rientra: il rientro lo scrive la riga, che conosce la
// propria profondità. Qui sarebbe cumulativo, perché questi contenitori si annidano.
const ChildrenContainer = styled.div`
    min-width: 0;
`;

// I GLIFI SI MISURANO QUI. Gli SVG della scena non portano `width`/`height` — se li
// portassero, SVGO toglierebbe il `viewBox` e la figura verrebbe disegnata a 1:1 dentro
// un riquadro di sedici pixel. Chi li ospita deve quindi dargli una misura: senza, in
// una riga flex l'immagine non ha larghezza intrinseca, viene compressa a zero e la
// riga sembra semplicemente priva di icona. Da qui anche `flex: none`.
const glyphBox = (size: number) => `
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: ${size}px;
    height: ${size}px;
    margin-right: 8px;
    user-select: none;

    svg {
        width: ${size}px;
        height: ${size}px;
        display: block;
    }
`;

// LA SPECIE COLORA IL SUO GLIFO: verde-acqua il soggetto, viola il raggruppamento. È un tratto di
// CIÒ CHE LA RIGA È, non del suo stato — per questo non cede all'arancio della selezione, che dice
// un'altra cosa («questa è scelta»), e che la riga presa porta già addosso in altri due modi.
//
// CHI LA SPECIE NON LA DICHIARA RESTA COM'ERA: le liste che non distinguono — le camere — non
// hanno una domanda a cui il colore risponda, e un colore che dice «questo è un oggetto» su una
// camera direbbe una cosa falsa.
const Icon = styled.span<{ $kind?: string }>`
    ${glyphBox(16)}
    color: ${(props) => (props.$kind === 'group' ? panel.kindGroupInk : props.$kind === 'mesh' ? panel.kindSubjectInk : 'inherit')};
`;
const CustomIconContainer = styled.div`
    ${glyphBox(18)}
`;
// I COMANDI DI RIGA SONO BOTTONI, non glifi sbiaditi: stessa misura e stesso fondo al passaggio
// dei comandi del Composer, così i due alberi si usano allo stesso modo.
const VisibilityIcon = styled.span<{ $mixed?: boolean }>`
    display: inline-flex;
    flex: none;
    width: 28px;
    height: 28px;
    align-items: center;
    justify-content: center;
    margin-right: 0;
    border-radius: 5px;
    cursor: pointer;
    color: ${(props) => (props.$mixed ? props.theme.app.component.button.primary.default.backgroundColor : 'inherit')};

    svg {
        width: 17px;
        height: 17px;
        display: block;
    }

    /* I glifi Material si riempiono, quelli della scena sono disegnati a contorno: riempirli
       cancellava il tratto e l'occhio «misto» diventava una macchia arancione. */
    svg:not([stroke]) {
        fill: currentColor;
    }

    &:hover {
        background: #e5e7eb;
    }
`;

// ACCESO SI VEDE. È lo stesso bottone dell'occhio, ma dichiara uno stato che in tutto l'elenco
// vale per una riga sola — stai guardando ATTRAVERSO quella camera — e uno stato unico va letto
// senza cercarlo: prende la tinta d'azione e resta acceso anche quando il mouse è altrove.
const RowToggleIcon = styled(VisibilityIcon)<{ $on: boolean }>`
    opacity: ${(props) => (props.$on ? 1 : 0.72)};
    color: ${(props) => (props.$on ? props.theme.app.component.button.primary.default.backgroundColor : 'inherit')};
`;
const SearchHit = styled.mark`
    background: ${(props) => props.theme.app.secondary}44;
    color: inherit;
    border-radius: 2px;
    padding: 0 1px;
`;

/**
 * ISOLARE DICE QUALI RIGHE, NON PERCHÉ. Con l'elenco ridotto a tre righe su ottanta la domanda
 * diventa subito la seconda — «e adesso quale pezzo di questo nome ho scritto io?» — e la si
 * risponde rileggendo, che è esattamente il lavoro che la ricerca doveva togliere.
 *
 * SI CERCA ANCHE PER COSE CHE NEL NOME NON CI SONO: negli oggetti la ricerca guarda pure il
 * materiale. Lì il nome non contiene niente da accendere, e infatti non si accende nulla — la
 * riga c'è perché ha corrisposto altrove, e dirlo con un colore inventato sarebbe una bugia.
 */
export const markMatches = (text: string, query?: string): React.ReactNode => {
    const needle = query?.trim().toLowerCase();
    if (!needle) return text;

    const hay = text.toLowerCase();
    const pieces: React.ReactNode[] = [];
    let from = 0;
    let at = hay.indexOf(needle);

    while (at !== -1) {
        if (at > from) pieces.push(text.slice(from, at));
        pieces.push(<SearchHit key={at}>{text.slice(at, at + needle.length)}</SearchHit>);
        from = at + needle.length;
        at = hay.indexOf(needle, from);
    }

    if (!pieces.length) return text;
    if (from < text.length) pieces.push(text.slice(from));

    return pieces;
};

// CHI CONTIENE SI LEGGE PIÙ FORTE DI CHI STA DENTRO. In un albero profondo la struttura la
// disegnano i rientri, ma i rientri si contano solo se si guarda il bordo sinistro: il grassetto
// dice a colpo d'occhio dove comincia un ramo, e lo dice al centro della riga, dove si sta già
// leggendo il nome.
const NodeName = styled.span<{ $branch?: boolean }>`
    user-select: none;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    font-weight: ${(props) => (props.$branch ? 600 : 400)};
    text-overflow: ellipsis;
    white-space: nowrap;
`;

// IL NOME SI CAMBIA DOVE SI LEGGE, come nella testata dell'Inspector: nessuna icona, nessuna
// finestra. Il gesto è quello di ogni gestore di file — si sceglie la riga, e un secondo clic sul
// nome lo apre — e vale sia per una riga sia per una cartella.
//
// IL CAMPO EREDITA IL CARATTERE DELLA RIGA e restituisce lo spazio della propria cornice, o il
// nome si sposterebbe di qualche pixel nel momento in cui lo si tocca: un testo che salta mentre
// lo stai per scrivere fa dubitare di aver cliccato la cosa giusta.
const NodeNameInput = styled.input`
    flex: 1;
    min-width: 0;
    margin: -2px -5px;
    padding: 1px 4px;
    border: 1px solid ${panel.accent};
    border-radius: 4px;
    background: ${panel.field};
    color: ${panel.ink};
    font: inherit;
    outline: none;
`;

// IL CODINO DELLA RIGA — un dato di contorno in coda al nome: quante mesh usano un materiale,
// di che specie è una luce. Sta DOPO il nome e prima dei comandi, ed è sempre visibile: non è
// un comando che compare al passaggio, è una cosa che si legge scorrendo.
//
// UNA PAROLA STA UN GRADINO PIÙ SOTTO DI UN NUMERO. «Hemispheric» a corpo pieno spinge il nome
// fuori dalla riga, e un nome troncato per un dato di contorno rovescia la gerarchia. Il numero
// invece è corto e va incolonnato, che è il solo modo perché una colonna di numeri si legga
// come una colonna.
/** Il dato di contorno in coda al nome di una riga: un numero, o una parola. */
export type TreeRowNote = { text: string; title?: string; word?: boolean };

const RowNote = styled.span<{ $word?: boolean }>`
    flex: none;
    margin-left: 6px;
    color: ${(props) => props.theme.app.textColor};
    opacity: 0.65;
    font-size: ${(props) => (props.$word ? '9.5px' : '10px')};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
`;

// I COMANDI DELLA RIGA STANNO IN FONDO E COMPAIONO AL PASSAGGIO. Una colonna di occhi
// accesi a sinistra di ogni nome pesa quanto i nomi e non dice niente: se tutto è visibile,
// l'informazione è zero. Qui l'occhio si mostra sulla riga che stai toccando — o su quella
// selezionata — e resta **fisso** su ciò che è nascosto, perché quello è l'unico caso in cui
// il segno porta un'informazione.
const RowTrailing = styled.span<{ $pinned: boolean }>`
    flex: none;
    display: flex;
    align-items: center;
    gap: 2px;
    margin-left: 6px;
    opacity: ${(props) => (props.$pinned ? 1 : 0)};
    transition: opacity 0.12s ease;

    ${TreeNodeContainer}:hover &,
    ${TreeNodeContainer}:focus-within & {
        opacity: 1;
    }
`;

// Interfaccia per l'handle del TreeComponent
export interface TreeHandle {
    closeOverlay: () => void;
    /**
     * Toglie la scelta e chiude il pannello della riga: è il clic sul vuoto, chiesto da fuori.
     * Facoltativo perché non tutti gli handle sono questo albero — la griglia dei materiali ne
     * costruisce uno suo, e lì il vuoto non significa ancora niente.
     */
    clearSelection?: () => void;
    selectAndOpenOverlay: (nodeId: string) => void;
    /** Apre il nome di una riga in scrittura. Lo usa chi crea: la cosa nasce, e il nome si dà dov'è scritto. */
    startRename: (nodeId: string) => void;
}

export interface TreeComponentProps {
    treeState: {
        moveSelection: any;
        nodes: any[];
        openNodes: Set<string>;
        selectedIds: Set<string>;
        draggedId: string | null;
        dropTargetId: string | null;
        overlayNodeId: string | null;
        flatVisibleList: any[];
        setDraggedId: (id: string | null) => void;
        setDropTargetId: (id: string | null) => void;
        setOverlayNodeId: (id: string | null) => void;
        findNodeById: (id: string) => any;
        findParentId: (id: string) => string | null;
        findNodeByDataID: (nodes: any[], dataID: string) => any;
        toggleSelection: (id: string, shiftKey: boolean, ctrlKey: boolean, order?: string[]) => void;
        toggleNode: (id: string) => void;
        toggleVisibility: (id: string) => void;
        setSelectedIds: (ids: Set<string>) => void;
        canTurnIntoFolder?: (node: any) => boolean;
    };
    // LA RICERCA NON STA QUI. È della SEZIONE, non dell'albero: nella griglia dei materiali
    // l'albero non c'è e il campo deve restare dov'era, quindi vive nel `SectionPanel`. Lo stesso
    // vale per «seleziona tutto», che è un comando fra i comandi da quando la barra esiste.
    // Restavano dentro qui un campo, una casella e quattro prop che nessuno accendeva più.
    treeLabel?: string;
    /** Cosa dire quando la lista è vuota: il glifo della sezione e una riga. */
    emptyIcon?: React.ReactNode;
    emptyMessage?: React.ReactNode;
    /**
     * «DOVE STA?» — la riga da portare in vista e far lampeggiare.
     *
     * ARRIVA PER PROP E NON DAL MANICO, ed è la correzione di un difetto vero: il manico è UNO
     * per quattro sezioni e chi lo lega per ultimo se lo prende, quindi una chiamata imperativa
     * poteva finire sull'albero sbagliato e non fare niente — in silenzio, che è il modo peggiore
     * di non funzionare. Una prop la riceve questo albero e nessun altro.
     *
     * IL GETTONE SERVE A CHIEDERE DUE VOLTE LA STESSA RIGA: senza, la seconda domanda sarebbe lo
     * stesso valore di prima e non succederebbe niente.
     */
    /**
     * La riga si dichiara in due modi, e sono due chi la chiede: dall'INTERNO della sezione si
     * conosce l'id del nodo — è la freccetta della testata — mentre da UN'ALTRA sezione si conosce
     * solo il `dataID` della cosa nel mondo 3D, perché il nodo dell'albero è una faccenda di chi
     * quell'albero ce l'ha.
     */
    blinkRequest?: { nodeId?: string; dataID?: string; token: number } | null;
    disableDragAndDrop?: boolean;
    onNodeMoved: (nodeId: string, targetParentId: string | null, index: number) => void;
    /** `nodeId` è l'id del nodo dell'albero e `nextVisible` lo stato che deve assumere. */
    onToggleNodeVisibility?: (nodeId: string, nextVisible: boolean) => void;
    showVisibilityIcon?: boolean; // New prop to control visibility icon display
    /**
     * IL LUCCHETTO: una riga bloccata non si sceglie dal palco e non si trasforma — si sceglie
     * solo da qui. Chi ospita l'albero dice se la specie che mostra può essere bloccata, e riceve
     * il gesto: lo stato lo tiene lui, perché è lui a parlare con la scena.
     */
    showLockIcon?: boolean;
    /** `nextLocked` è lo stato che la riga deve assumere, non quello che ha. */
    onToggleNodeLock?: (nodeId: string, nextLocked: boolean) => void;
    /** Il testo cercato adesso, quello con cui la sezione ha già ridotto l’elenco: qui si accende. */
    highlight?: string;
    /**
     * LA LISTA È PIATTA: lasciare una riga su una riga vuol dire **metterla lì**, non infilarla
     * dentro. Le luci sono così — una luce non contiene una luce — e prima quel gesto non faceva
     * niente del tutto: il nodo dichiarava `notTurnableIntoFolder` e il drop veniva annullato in
     * silenzio, mentre nel vecchio scene editor annidava una luce dentro l'altra, che è una cosa
     * che nella scena non significa niente.
     *
     * Con questo acceso l'albero **non tocca la struttura**: dice soltanto a chi lo ospita su
     * quale riga è caduto il trascinamento, e l'ordine nuovo lo scrive lui — dove lo sa salvare.
     * Per questo qui `onNodeMoved` riceve la riga BERSAGLIO e non un genitore.
     */
    flatList?: boolean;
    /**
     * I COMANDI CHE VOGLIONO UN SOGGETTO, riga per riga. Chi monta l'albero li costruisce già
     * legati al nodo che riceve: il menu non sceglie niente aprendosi, quindi il soggetto deve
     * essere quello sotto il «…» e non la selezione, che potrebbe stare altrove.
     */
    rowActions?: (node: any) => SceneEditorAction[];
    /**
     * IL CODINO DI UNA RIGA, quando quella lista ne ha uno da dire. Restituire `null` per le
     * righe che non lo hanno — una cartella fra i materiali, un gruppo fra le luci — invece di
     * una stringa vuota, che occuperebbe comunque il suo spazio. `word` per un'etichetta, che sta
     * un gradino sotto un numero.
     */
    rowNote?: (node: any) => TreeRowNote | null | undefined;
    /**
     * UN SECONDO SEGNO DI STATO NELLA RIGA, per le liste che ne hanno due. L'occhio dice se la
     * riga si vede; questo dice qualcos'altro che è vero di lei sola — per una camera, se stai
     * guardando ATTRAVERSO. Sta PRIMA dell'occhio perché è il più specifico dei due, e l'ordine
     * è quello del mockup. Restituire `null` per le righe che non lo hanno.
     */
    rowToggle?: (node: any) => { icon: React.ReactNode; label: string; active: boolean; onClick: () => void } | null;
    /**
     * RINOMINARE SUL POSTO. Chi la dichiara abilita il gesto sulle proprie righe: un secondo clic
     * sul nome di una riga già scelta lo apre in scrittura. Riceve il nodo e non il solo
     * identificativo, perché una lista può doverne distinguere le specie — un gruppo di materiali
     * non si rinomina come un materiale.
     */
    onNodeRename?: (node: any, name: string) => void;
    disableOverlay?: boolean;
    onSelectedComponentChange?: (component: React.ReactNode | null) => void;
    /**
     * CERCARE APRE I RAMI. Un albero mostra i figli solo dei nodi aperti, e va bene finché si
     * naviga: si scende dove si vuole andare. Ma con una ricerca in corso quella regola nasconde
     * proprio ciò che si sta cercando — chi filtra vede restare i soli antenati, chiusi, e da fuori
     * sembra che la ricerca non trovi niente.
     *
     * Chi ospita l'albero lo accende quando un filtro è attivo: le righe rimaste sono già poche e
     * sono tutte pertinenti, quindi tenerle chiuse non risparmia niente a nessuno.
     */
    expandAll?: boolean;
}

const TreeComponent = forwardRef<TreeHandle, TreeComponentProps>(
    (
        {
            treeState,
            treeLabel = 'Scene items',
            emptyIcon,
            emptyMessage,
            blinkRequest,
            disableDragAndDrop,
            onNodeMoved,
            onToggleNodeVisibility,
            showVisibilityIcon = true,
            showLockIcon,
            onToggleNodeLock,
            highlight,
            flatList,
            rowActions,
            rowNote,
            rowToggle,
            onNodeRename,
            disableOverlay,
            onSelectedComponentChange,
            expandAll
        },
        ref
    ) => {
        const {
            nodes,
            openNodes,
            selectedIds,
            draggedId,
            dropTargetId,
            overlayNodeId,
            setDraggedId,
            setDropTargetId,
            setOverlayNodeId,
            findNodeById,
            findParentId,
            findNodeByDataID,
            toggleSelection,
            toggleNode,
            toggleVisibility,
            setSelectedIds,
            flatVisibleList
        } = treeState;

        const treeContainerRef = useRef<HTMLDivElement>(null);

        const [dragPreview, setDragPreview] = useState<{
            x: number;
            y: number;
            label: string;
        } | null>(null);

        // IL PANNELLO CONSEGNATO INVECCHIAVA, per due ragioni che si assomigliano. Quando la colonna
        // di destra è di qualcun altro (`disableOverlay`), il pannello di una riga si consegna al clic
        // e resta lì: rinominare la riga cambiava il nodo e non il pannello, e il nome nuovo si vedeva
        // solo chiudendo e riaprendo. Da qui si ripubblica ogni volta che l'albero cambia — e se ne va
        // quando la riga che descriveva non c'è più.
        //
        // L'altra ragione la copre `publishable`: si consegna la FABBRICA del pannello e non il
        // pannello, perché fra un ridisegno e l'altro cambia anche ciò di cui l'albero non si accorge,
        // cioè il gizmo che sposta l'oggetto in scena.
        const publishComponent = useRef(onSelectedComponentChange);
        publishComponent.current = onSelectedComponentChange;

        // E NON SI PUBBLICA IL VUOTO SE NON SI AVEVA NIENTE DA DIRE. La colonna di destra è UNA
        // per tutte le sezioni, ma gli alberi che ci scrivono dentro sono tanti — e non tutti sono
        // in vista: la libreria dei materiali resta montata mentre guardi gli oggetti, perché
        // trasloca invece di rinascere. Un albero fermo, senza niente di aperto, pubblicava `null`
        // a ogni ridisegno: cancellava il pannello che un'ALTRA sezione aveva appena aperto, e da
        // fuori era «clicco una riga e non succede niente».
        //
        // Qui il vuoto lo pubblica solo chi prima aveva pubblicato qualcosa — cioè chi sta davvero
        // chiudendo il proprio pannello. Chi non ha mai scritto in quella colonna non ha titolo per
        // cancellarla.
        const publishedNodeId = useRef<string | null>(null);

        useEffect(() => {
            if (!disableOverlay) return;

            const openNode = overlayNodeId ? findNodeById(overlayNodeId) : undefined;

            if (!openNode?.component) {
                // SI TIENE IL VECCHIO FINCHÉ NON ARRIVA IL NUOVO. Se qualcuno dichiara ancora di avere
                // una riga aperta ma quella riga non si trova, non è una chiusura: è una sostituzione a
                // metà strada — la luce di prima è stata rifatta e la sua erede sta per comparire.
                // Svuotare la colonna in quel varco la fa richiudere e riaprire, e la scena 3D accanto
                // si allarga e si restringe due volte per un gesto solo.
                //
                // Il vuoto lo pubblica solo chi non ha più NIENTE di aperto, cioè chi ha chiuso davvero.
                if (overlayNodeId) return;

                if (publishedNodeId.current !== null) {
                    publishedNodeId.current = null;
                    publishComponent.current?.(null);
                }
                return;
            }

            publishedNodeId.current = overlayNodeId;

            publishComponent.current?.(publishable(openNode.component));
            // `findNodeById` cambia identità esattamente quando cambia l'albero: è lui a dire
            // «qualcosa si è mosso, il pannello va rifatto».
        }, [overlayNodeId, findNodeById, disableOverlay]);

        // «DOVE STA?» — la riga si accende e si spegne tre volte, e prima di tutto si porta in vista.
        //
        // Non cambia la selezione e non apre niente: è una risposta, non un comando. Chi la chiede è
        // la freccetta nella testata dell'Inspector — sei già sul soggetto, e vuoi sapere dove sta
        // nella colonna — ma la usa anche chi arriva da un altro elenco, dopo aver selezionato.
        const [blinkingId, setBlinkingId] = useState<string | null>(null);

        // IL GESTO STA IN UN POSTO SOLO — secondo clic, Invio, Esc, uscita dal campo — ed è lo stesso
        // con cui si rinomina una tessera della griglia: `useInlineRename`.
        const rename = useInlineRename();

        // A OGNI DOMANDA SI RISPONDE UNA VOLTA SOLA, ma si può rispondere in ritardo. Arrivando da
        // un'altra sezione la domanda precede l'albero: questo componente si sta montando proprio
        // adesso e le righe non ci sono ancora. Finché la riga non si trova il gettone non si segna,
        // e l'effetto riprova a ogni cambio dell'elenco — cioè appena l'albero si riempie.
        //
        // È il pezzo che prima mancava: chiedere «dove sta» passando di sezione andava a vuoto la
        // prima volta, e funzionava alla seconda perché a quel punto la sezione era già montata.
        const servedBlink = useRef(0);

        useEffect(() => {
            if (!blinkRequest || blinkRequest.token === servedBlink.current) return;

            const found = blinkRequest.nodeId ?? findNodeByDataID(nodes, blinkRequest.dataID ?? '')?.id;
            if (!found) return;

            servedBlink.current = blinkRequest.token;
            const nodeId = found;

            // I RAMI CHIUSI SI APRONO LUNGO LA STRADA. Una riga dentro un gruppo chiuso non esiste a
            // schermo: farla lampeggiare senza aprirle la strada è rispondere «è là dentro» a chi ha
            // chiesto dove sta.
            const closed: string[] = [];
            let walking: string | null = nodeId;

            while (walking) {
                const parentId: string | null = findParentId(walking);
                if (parentId && !openNodes.has(parentId)) closed.push(parentId);
                walking = parentId;
            }

            closed.forEach((id) => toggleNode(id));

            setBlinkingId(nodeId);

            // La riga può essere comparsa proprio adesso — un ramo appena aperto — quindi si cerca
            // dopo che il browser ha disegnato.
            requestAnimationFrame(() => {
                const target = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(String(nodeId)) : nodeId;
                treeContainerRef.current?.querySelector(`[data-node-id="${target}"]`)?.scrollIntoView({ block: 'nearest' });
            });

            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [blinkRequest, nodes]);

        // QUANTO DURA STA IN UN EFFETTO SUO. Insieme al gesto, il cronometro ripartiva da capo a ogni
        // cambio dell'elenco — e peggio: quando l'effetto ripartiva per una domanda già servita,
        // spegneva il cronometro senza rimetterlo, e la riga restava accesa per sempre. Il tempo
        // dipende solo da quale riga sta lampeggiando.
        useEffect(() => {
            if (!blinkingId) return;

            const timer = window.setTimeout(() => setBlinkingId(null), BLINK_DURATION);
            return () => window.clearTimeout(timer);
        }, [blinkingId]);

        // Esponi i metodi tramite useImperativeHandle
        useImperativeHandle(
            ref,
            () => ({
                closeOverlay: () => {
                    setOverlayNodeId(null);
                    onSelectedComponentChange?.(null);
                },
                // «NIENTE DI TUTTO QUESTO», chiesto da fuori. Il vuoto sotto le righe è dell'albero
                // finché l'albero riempie il pannello; sotto, il vuoto è di chi lo ospita, e un clic
                // là deve valere lo stesso — altrimenti deselezionare dipende da quante righe hai.
                clearSelection: () => {
                    setSelectedIds(new Set());
                    setOverlayNodeId(null);
                },
                // APRIRE IL NOME DI UNA RIGA DA FUORI: serve a chi crea qualcosa e vuole che il nome
                // sia già in scrittura, senza far passare da una finestra.
                startRename: (nodeId: string) => {
                    const target = findNodeById(nodeId);
                    if (target) rename.start(nodeId, target.name);
                },
                selectAndOpenOverlay: (dataID: string) => {
                    // 1) Usa la ricerca ricorsiva su tutti i rami dell'albero
                    const targetNode = findNodeByDataID(treeState.nodes, dataID);

                    if (!targetNode) {
                        console.warn(`Nessun nodo trovato per meshId ${dataID}`);
                        return;
                    }

                    if (!targetNode.doNotOpenOverlay && targetNode.component) {
                        onSelectedComponentChange?.(publishable(targetNode.component));
                    }

                    // 2) Trova tutti i nodi padre che devono essere aperti
                    const nodesToOpen: string[] = [];
                    let currentId = targetNode.id;

                    while (currentId) {
                        const parentId = findParentId(currentId);
                        if (parentId) {
                            // Aggiungi solo se non è già aperto
                            if (!openNodes.has(parentId)) {
                                nodesToOpen.push(parentId);
                            } else {
                            }
                            currentId = parentId;
                        } else {
                            break;
                        }
                    }

                    // 3) Apri solo i nodi padre che NON sono già aperti
                    nodesToOpen.forEach((nodeId) => {
                        toggleNode(nodeId);
                    });

                    // 4) Selezione
                    setSelectedIds(new Set([targetNode.id]));

                    // 5) Apertura overlay
                    if (targetNode.component && !targetNode.doNotOpenOverlay) {
                        setOverlayNodeId(targetNode.id);
                    }
                }
            }),
            // aggiungo queste dipendenze perchè ad esempio, usando openNodes nella selectAndOpenOverlay, prendendo il valore alla creazione dell'imperative,
            // openNodes restava sempre vuoto, quindi ho aggiunto ognuno per:
            // openNodes - per controllare quali nodi sono aperti
            // findNodeByDataID - per trovare il nodo target
            // findParentId - per risalire la catena dei parent
            // toggleNode - per aprire i nodi
            // setSelectedIds - per selezionare il nodo
            // setOverlayNodeId - per aprire l'overlay
            // treeState.nodes - per la ricerca nel tree
            // findNodeById - per leggere il nome da cui parte la scrittura
            [
                setOverlayNodeId,
                openNodes,
                findNodeByDataID,
                findNodeById,
                findParentId,
                toggleNode,
                setSelectedIds,
                treeState.nodes,
                onSelectedComponentChange,
                rename
            ]
        );

        // Gestori Drag and Drop corretti
        const resetDragState = useCallback(() => {
            setDraggedId(null);
            setDropTargetId(null);
            setDragPreview(null);
        }, [setDraggedId, setDropTargetId]);

        // 1) drag start
        const handleDragStart = useCallback(
            (e: React.DragEvent, id: string) => {
                e.stopPropagation();
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', id);

                // Se il nodo non è selezionato, selezionalo
                if (!selectedIds.has(id)) {
                    setSelectedIds(new Set([id]));
                }

                setDraggedId(id);

                // Usa il primo nodo selezionato per il preview
                const draggedNodes = selectedIds.has(id) ? Array.from(selectedIds) : [id];
                const first = draggedNodes[0];
                const n = findNodeById(first);

                if (n) {
                    const label = draggedNodes.length > 1 ? `${n.name} (+${draggedNodes.length - 1} others)` : n.name;
                    setDragPreview({
                        x: e.clientX + 8,
                        y: e.clientY + 8,
                        label
                    });
                }
            },
            [selectedIds, findNodeById, setSelectedIds]
        );

        // 2) drag over
        const handleDragOver = useCallback(
            (e: React.DragEvent, id: string) => {
                e.preventDefault();
                e.stopPropagation();

                // Non permettere il drop su un nodo che è tra quelli draggati
                const draggedNodes = draggedId && selectedIds.has(draggedId) ? Array.from(selectedIds) : draggedId ? [draggedId] : [];

                if (draggedNodes.includes(id)) {
                    e.dataTransfer.dropEffect = 'none';
                    return;
                }

                // Verifica che non stiamo droppando un nodo su un suo discendente
                if (draggedId && treeState.findParentId) {
                    let currentParent = treeState.findParentId(id);
                    while (currentParent) {
                        if (draggedNodes.includes(currentParent)) {
                            e.dataTransfer.dropEffect = 'none';
                            return;
                        }
                        currentParent = treeState.findParentId(currentParent);
                    }
                }

                e.dataTransfer.dropEffect = 'move';
                setDropTargetId(id);
            },
            [draggedId, selectedIds, treeState]
        );

        // 3) drop su un nodo
        const handleDrop = useCallback(
            (e: React.DragEvent, targetId: string) => {
                e.preventDefault();
                e.stopPropagation();

                if (!draggedId || !treeState.moveSelection) {
                    resetDragState();
                    return;
                }

                // Determina quali nodi spostare
                const nodesToMove = selectedIds.has(draggedId) ? Array.from(selectedIds) : [draggedId];

                // Non permettere il drop su se stesso o sui propri discendenti
                if (nodesToMove.includes(targetId)) {
                    resetDragState();
                    return;
                }

                // Verifica discendenti
                let isInvalidDrop = false;
                if (treeState.findParentId) {
                    let currentParent = treeState.findParentId(targetId);
                    while (currentParent && !isInvalidDrop) {
                        if (nodesToMove.includes(currentParent)) {
                            isInvalidDrop = true;
                        }
                        currentParent = treeState.findParentId(currentParent);
                    }
                }

                if (isInvalidDrop) {
                    resetDragState();
                    return;
                }

                // In una lista piatta non c'è dentro dove entrare: il trascinamento dice solo dove
                // va messa la riga, e chi ospita l'albero scrive il nuovo ordine.
                if (flatList) {
                    onNodeMoved(nodesToMove[0], targetId, 0);
                    resetDragState();
                    return;
                }

                // 🔧 NUOVO: Controlla se il nodo target può diventare cartella
                const targetNode = treeState.findNodeById(targetId);
                if (targetNode && treeState.canTurnIntoFolder && !treeState.canTurnIntoFolder(targetNode)) {
                    console.warn('Cannot drop on node that cannot turn into folder:', targetNode.name);
                    resetDragState();
                    return;
                }

                // Esegui effettivamente lo spostamento nel TreeNodeManager
                treeState.moveSelection(nodesToMove, targetId);

                // Chiamiamo il callback onNodeMoved per notificare il componente parent
                const targetChildren = targetNode?.children ?? [];
                const insertionIndex = targetChildren.length;
                onNodeMoved(nodesToMove[0], targetId, insertionIndex);

                // Assicurati che il target sia espanso se ha ricevuto dei figli
                if (!openNodes.has(targetId)) {
                    toggleNode(targetId);
                }

                resetDragState();
            },
            [draggedId, selectedIds, treeState, openNodes, toggleNode, resetDragState, onNodeMoved, flatList]
        );

        // TORNARE ALLA RADICE È UNO SPOSTAMENTO COME GLI ALTRI, e per un po' non lo è stato:
        // l'albero spostava il nodo in cima da solo e non avvisava nessuno. A schermo la riga usciva
        // dal suo ramo, ma nella scena la mesh restava figlia di prima — e al primo ridisegno tornava
        // dov'era, come se il gesto non fosse mai avvenuto. `onNodeMoved` con un genitore `null` è il
        // modo in cui questo albero dice «adesso è di nessuno», e chi lo ospita sa cosa farne.
        const dropOnRoot = useCallback(() => {
            if (!draggedId || !treeState.moveSelection) return;

            const nodesToMove = selectedIds.has(draggedId) ? Array.from(selectedIds) : [draggedId];
            treeState.moveSelection(nodesToMove, null);
            onNodeMoved(nodesToMove[0], null, 0);
        }, [draggedId, selectedIds, treeState, onNodeMoved]);

        // 4) dragEnd → fuori dal container
        const handleDragEnd = useCallback(
            (e: React.DragEvent) => {
                // Fuori dall'elenco, in una lista piatta, non c'è una radice in cui tornare: si è
                // già tutti lì.
                if (!draggedId || !treeState.moveSelection || flatList) {
                    resetDragState();
                    return;
                }

                const rect = treeContainerRef.current?.getBoundingClientRect();
                if (rect) {
                    const { clientX: x, clientY: y } = e;
                    const outside = x < rect.left || x > rect.right || y < rect.top || y > rect.bottom;

                    if (outside) dropOnRoot();
                }

                resetDragState();
            },
            [draggedId, treeState, resetDragState, flatList, dropOnRoot]
        );

        // E LO SPAZIO VUOTO DELL'ELENCO È LA RADICE. Lasciare una riga sotto l'ultima è il gesto con
        // cui si tira fuori una mesh da tutto — più naturale che portarla fuori dal pannello — e
        // prima non faceva niente: il contenitore non era un bersaglio, quindi il browser rifiutava
        // il rilascio. Le righe fermano il proprio drop, quindi qui arriva solo ciò che è caduto nel
        // vuoto.
        const handleRootDragOver = useCallback(
            (e: React.DragEvent) => {
                if (!draggedId || flatList) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
            },
            [draggedId, flatList]
        );

        const handleRootDrop = useCallback(
            (e: React.DragEvent) => {
                if (!draggedId || flatList) return;
                e.preventDefault();
                dropOnRoot();
                resetDragState();
            },
            [draggedId, flatList, dropOnRoot, resetDragState]
        );

        // 5) drag leave - per resettare il drop target quando usciamo da un nodo
        const handleDragLeave = useCallback(
            (e: React.DragEvent, id: string) => {
                e.preventDefault();
                e.stopPropagation();

                // Resetta il drop target solo se stiamo realmente uscendo dal nodo
                // e non entrando in un suo figlio
                const rect = e.currentTarget.getBoundingClientRect();
                const { clientX: x, clientY: y } = e;
                const outside = x < rect.left || x > rect.right || y < rect.top || y > rect.bottom;

                if (outside && dropTargetId === id) {
                    setDropTargetId(null);
                }
            },
            [dropTargetId]
        );

        // Effetto per aggiornare la posizione del drag preview
        useEffect(() => {
            const onDragOverWindow = (e: DragEvent) => {
                if (dragPreview) {
                    setDragPreview({
                        ...dragPreview,
                        x: e.clientX + 12,
                        y: e.clientY + 12
                    });
                }
            };

            window.addEventListener('dragover', onDragOverWindow);
            return () => window.removeEventListener('dragover', onDragOverWindow);
        }, [dragPreview]);

        // Cleanup quando il componente viene smontato
        useEffect(() => {
            return () => {
                resetDragState();
            };
        }, [resetDragState]);

        // Funzione di rendering ricorsivo
        const renderTree = useCallback(
            (node: any, depth = 0, parent: string | null = null) => {
                const isFolder = node.children?.length > 0;
                const isOpen = expandAll || openNodes.has(node.id);

                const groupVis = isFolder ? groupState(branchOf(node), isShown) : null;
                const isVis = isFolder ? groupVis !== 'none' : node.visible !== false;
                const isMixed = groupVis === 'some';

                // UN GRUPPO È BLOCCATO SE LO SONO TUTTI I SUOI, come per l'occhio: il contenitore non
                // ha un lucchetto suo, ha il riassunto di quelli che contiene.
                const groupLock = isFolder ? groupState(leavesOf(node), isShut) : null;
                const isLocked = isFolder ? groupLock === 'all' : node.locked === true;
                const isLockMixed = groupLock === 'some';
                const isSel = selectedIds.has(node.id);

                return (
                    <div key={node.id}>
                        <TreeNodeContainer
                            data-node-id={node.id}
                            onDragOver={(e) => handleDragOver(e, node.id)}
                            onDragLeave={(e) => handleDragLeave(e, node.id)}
                            onDrop={(e) => handleDrop(e, node.id)}
                            onDragEnd={handleDragEnd}
                            $depth={depth}
                            $isDragging={draggedId === node.id}
                            $isDragover={dropTargetId === node.id}
                            $selected={isSel}
                            $isVisible={isVis}
                            $blinking={blinkingId === node.id}
                            role='treeitem'
                            aria-selected={isSel}
                            aria-level={depth + 1}
                            aria-expanded={isFolder ? isOpen : undefined}
                            tabIndex={isSel ? 0 : -1}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    toggleSelection(node.id, false, false);
                                }
                                if (isFolder && e.key === 'ArrowRight' && !isOpen) {
                                    e.preventDefault();
                                    toggleNode(node.id);
                                }
                                if (isFolder && e.key === 'ArrowLeft' && isOpen) {
                                    e.preventDefault();
                                    toggleNode(node.id);
                                }
                            }}
                            onClick={(e) => {
                                node.onNodeClick?.();
                                e.stopPropagation();
                                toggleSelection(node.id, e.shiftKey, e.ctrlKey || e.metaKey);

                                if (node.doNotOpenOverlay) {
                                    setOverlayNodeId(null);
                                    onSelectedComponentChange?.(null);
                                    return;
                                }

                                if (node.component) {
                                    setOverlayNodeId(node.id);
                                    onSelectedComponentChange?.(publishable(node.component));
                                }
                            }}
                        >
                            {!isFolder && <TreeTwistySlot aria-hidden='true' />}
                            {isFolder && (
                                <TreeTwisty
                                    type='button'
                                    $isOpen={isOpen}
                                    aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${node.name}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleNode(node.id);
                                    }}
                                >
                                    {isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}
                                </TreeTwisty>
                            )}

                            {node.customIcon ? (
                                <CustomIconContainer>{node.customIcon}</CustomIconContainer>
                            ) : (
                                /* Il glifo dice CHE COSA è il nodo. Se chi costruisce l'albero
                                   lo sa — l'Outliner distingue le mesh vere dai nodi di
                                   trasformazione — lo dichiara in `kind`; le altre liste non
                                   hanno quella distinzione e ricadono sulla forma dell'albero. */
                                <Icon $kind={node.kind}>{(node.kind ?? (isFolder ? 'group' : 'mesh')) === 'group' ? ICONS.folderClosed : ICONS.file}</Icon>
                            )}

                            {rename.editingId === node.id ? (
                                <NodeNameInput aria-label={T._('Name', 'Admin')} {...rename.fieldProps(node.name, (value) => onNodeRename?.(node, value))} />
                            ) : (
                                <NodeName
                                    $branch={isFolder}
                                    onClick={(event) => {
                                        // IL SECONDO CLIC, NON IL DOPPIO: si apre solo il nome di una riga già scelta,
                                        // e solo se è scelta da sola — con un mucchio selezionato il clic serve ancora a
                                        // ridurre la scelta a questa riga.
                                        if (!onNodeRename || !isSel || selectedIds.size !== 1) return;

                                        event.stopPropagation();
                                        rename.start(node.id, node.name);
                                    }}
                                >
                                    {markMatches(node.name, highlight)}
                                </NodeName>
                            )}

                            {(() => {
                                const note = rowNote?.(node);
                                if (!note?.text) return null;

                                return (
                                    <RowNote $word={note.word} title={note.title}>
                                        {note.text}
                                    </RowNote>
                                );
                            })()}

                            {(showVisibilityIcon || rowActions || rowToggle) && (
                                <RowTrailing $pinned={!isVis || isMixed || isLocked || isLockMixed || isSel || !!rowToggle?.(node)?.active}>
                                    {(() => {
                                        const toggle = rowToggle?.(node);
                                        if (!toggle) return null;

                                        return (
                                            <RowToggleIcon
                                                role='button'
                                                aria-label={toggle.label}
                                                aria-pressed={toggle.active}
                                                title={toggle.label}
                                                tabIndex={0}
                                                $on={toggle.active}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggle.onClick();
                                                }}
                                            >
                                                {toggle.icon}
                                            </RowToggleIcon>
                                        );
                                    })()}

                                    {showLockIcon && (
                                        <VisibilityIcon
                                            role='button'
                                            aria-label={
                                                isFolder
                                                    ? `${isLocked ? 'Unlock' : 'Lock'} all in ${node.name}`
                                                    : `${isLocked ? 'Unlock' : 'Lock'} ${node.name}`
                                            }
                                            aria-pressed={isLocked}
                                            tabIndex={0}
                                            $mixed={isLocked || isLockMixed}
                                            onClick={(e) => {
                                                e.stopPropagation();

                                                const nextLocked = !isLocked;

                                                (isFolder ? leavesOf(node) : [node]).forEach((target: any) => {
                                                    if ((target.locked === true) === nextLocked) return;

                                                    onToggleNodeLock?.(target.id, nextLocked);
                                                });
                                            }}
                                        >
                                            {isLocked ? ICONS.locked : ICONS.unlocked}
                                        </VisibilityIcon>
                                    )}

                                    {showVisibilityIcon && (
                                        <VisibilityIcon
                                            role='button'
                                            aria-label={
                                                isFolder
                                                    ? `${groupVis === 'none' ? 'Show' : 'Hide'} all in ${node.name}`
                                                    : `${isVis ? 'Hide' : 'Show'} ${node.name}`
                                            }
                                            tabIndex={0}
                                            $mixed={isMixed}
                                            onClick={(e) => {
                                                e.stopPropagation();

                                                // Si passa lo stato VOLUTO, non quello attuale: chi
                                                // ascolta deve applicarlo, non ricalcolarlo.
                                                const nextVisible = isFolder ? groupVis === 'none' : !isVis;

                                                // UN GRUPPO SI SPEGNE SPEGNENDO CIÒ CHE CONTIENE, riga per riga:
                                                // così ogni figlio dice da sé di essere spento. Spegnendo il solo
                                                // contenitore, aprirlo avrebbe mostrato figli accesi che in scena
                                                // però non si vedono. Il contenitore però è dentro il conto: se ha
                                                // una geometria sua, restava a schermo a ramo spento.
                                                (isFolder ? branchOf(node) : [node]).forEach((target: any) => {
                                                    if ((target.visible !== false) === nextVisible) return;

                                                    toggleVisibility(target.id);
                                                    onToggleNodeVisibility?.(target.id, nextVisible);
                                                });
                                            }}
                                        >
                                            {isMixed ? ICONS.eyeMixed : isVis ? ICONS.eye : ICONS.eyeOff}
                                        </VisibilityIcon>
                                    )}

                                    {/* Il menu riceve i comandi GIÀ legati a questa riga: aprirlo non
                                        cambia la selezione, quindi il soggetto non può che essere lei. */}
                                    {rowActions && <CommandMenu actions={rowActions(node)} />}

                                    {!disableDragAndDrop && (
                                        <TreeDragHandle
                                            type='button'
                                            aria-label={`${T._('Drag to reorder', 'Admin')} ${node.name}`}
                                            draggable={rename.editingId !== node.id}
                                            onClick={(e) => e.stopPropagation()}
                                            onDragStart={(e) => {
                                                e.stopPropagation();
                                                const row = (e.currentTarget as HTMLElement).closest('[data-node-id]') as HTMLElement | null;
                                                setComposerRowDragImage(e.dataTransfer, row);
                                                handleDragStart(e, node.id);
                                            }}
                                            onDragEnd={handleDragEnd}
                                        >
                                            <DragIcon />
                                        </TreeDragHandle>
                                    )}
                                </RowTrailing>
                            )}
                        </TreeNodeContainer>

                        {/* SPEGNERE NON È CHIUDERE. La condizione chiedeva anche che il ramo fosse acceso,
                            e un gruppo spento smetteva di disegnare i propri figli: da fuori l'occhio
                            COLLASSAVA il ramo invece di nascondere ciò che contiene, e per rivedere cosa ci
                            fosse dentro bisognava riaccenderlo. Le righe adesso restano, spente: sono loro a
                            dire com'è la scena. */}
                        {isFolder && isOpen && node.children && (
                            <ChildrenContainer>
                                {[...node.children]
                                    .sort((a: any, b: any) => (a.displayOrder || 0) - (b.displayOrder || 0))
                                    .map((child: any) => renderTree(child, depth + 1, node.id))}
                            </ChildrenContainer>
                        )}
                    </div>
                );
            },
            [
                openNodes,
                selectedIds,
                // QUESTA FUNZIONE DISEGNA ANCHE IL LAMPEGGIO, quindi deve rinascere quando la riga accesa
                // cambia. Senza, `renderTree` resta quello di prima e continua a disegnare tutte le righe
                // spente: il lampeggio si vedeva solo per caso, quando entro quel secondo cambiava
                // qualcos'altro qui dentro — un ramo che si apriva, la sezione che cambiava. Da fuori era
                // «devo cliccare due volte».
                blinkingId,
                draggedId,
                dropTargetId,
                disableDragAndDrop,
                toggleSelection,
                toggleNode,
                toggleVisibility,
                onToggleNodeVisibility,
                showLockIcon,
                onToggleNodeLock,
                handleDragStart,
                handleDragOver,
                handleDrop,
                handleDragEnd,
                setOverlayNodeId,
                highlight,
                rowActions,
                rename,
                onNodeRename,
                expandAll
            ]
        );

        return (
            <TreeNodeInnerContainer
                ref={treeContainerRef}
                role='tree'
                aria-label={treeLabel}
                onDragOver={handleRootDragOver}
                onDrop={handleRootDrop}
                /* IL VUOTO SOTTO LE RIGHE È UNA RISPOSTA: cliccarlo vuol dire «niente di tutto questo».
                   Il contenitore arriva in fondo al pannello, quindi quel vuoto c'è sempre — e finora
                   era l'unico posto dell'elenco in cui un clic non voleva dire niente.

                   SOLO IL VUOTO VERO, cioè il contenitore in persona: una riga che lasciasse passare il
                   clic non sta dicendo «niente», sta dicendo sé stessa.

                   E CHIUDE ANCHE IL PANNELLO, perché «niente di tutto questo» non ammette eccezioni:
                   lasciarlo aperto voleva dire togliere la riga dall'elenco e continuare a descriverla
                   nella colonna di destra — da fuori, l'oggetto sembrava ancora scelto. Vale per il
                   pannello aperto qui dentro e per quello consegnato all'Inspector. */
                onClick={(event) => {
                    if (event.target !== event.currentTarget || (!selectedIds.size && !overlayNodeId)) return;

                    setSelectedIds(new Set());
                    setOverlayNodeId(null);
                }}
                /* SI PRENDE TUTTO CON CTRL+A, e «tutto» è quello che l'elenco sta MOSTRANDO: dopo il
                   filtro, e senza i figli dei rami chiusi. È la regola dei gestori di file — si prende
                   ciò che si vede — e per prenderne di più si apre il ramo.

                   STA SUL CONTENITORE E NON SULLA FINESTRA: la stessa combinazione, dentro il campo di
                   ricerca, deve continuare a selezionare il testo. Il contenitore prende il fuoco anche
                   cliccando il vuoto, che è il gesto con cui si arriva qui. */
                tabIndex={-1}
                onKeyDown={(event) => {
                    if ((event.key !== 'a' && event.key !== 'A') || (!event.ctrlKey && !event.metaKey)) return;

                    event.preventDefault();
                    setSelectedIds(new Set(flatVisibleList.map((node: any) => node.id)));
                }}
            >
                {/* La COPIA prima di ordinare non è pignoleria: `sort` riordina sul posto, e questo
                    array arriva dal `useMemo` di chi ci ospita — riordinarlo qui vuol dire cambiare
                    uno stato memoizzato durante il render di un altro. */}
                {nodes.length === 0 && emptyMessage ? (
                    <SceneEmptyState icon={emptyIcon}>{emptyMessage}</SceneEmptyState>
                ) : (
                    <>
                        {[...nodes].sort((a: any, b: any) => (a.displayOrder || 0) - (b.displayOrder || 0)).map((node: any) => renderTree(node, 0, null))}

                        {/* LE STESSE PAROLE IN OGNI ELENCO. Un gesto che si impara una volta va detto una volta
                            sola: se ogni lista lo spiegasse a modo suo, sembrerebbero gesti diversi. */}
                        <SelectionHint>
                            {keyText(T._('⇧click for a range', 'Admin'))} · {keyText(T._('⌘click one by one', 'Admin'))}
                        </SelectionHint>
                    </>
                )}

                {dragPreview && (
                    <DragPreviewLabel x={dragPreview.x} y={dragPreview.y}>
                        {dragPreview.label}
                    </DragPreviewLabel>
                )}

                {overlayNodeId && !disableOverlay && (
                    <OverlayPanel
                        onClose={() => {
                            setOverlayNodeId(null);
                            onSelectedComponentChange?.(null);
                        }}
                    >
                        {(() => {
                            const node = findNodeById(overlayNodeId);
                            if (!node?.component) return <div>No component selected.</div>;

                            return typeof node.component === 'function' ? node.component() : node.component;
                        })()}
                    </OverlayPanel>
                )}
            </TreeNodeInnerContainer>
        );
    }
);

export default TreeComponent;
